import type React from "react";
import { createContext, useContext, useEffect, useState } from "react";
import type { Tenant } from "@/lib/api";
import { useAuth } from "./AuthContext";

interface Subscription {
	id: string;
	planId: string;
	status: string;
	billingCycle: string;
	currentPeriodStart: string;
	currentPeriodEnd: string;
	trialEnd?: string;
	cancelAtPeriodEnd: boolean;
}

interface Plan {
	id: string;
	name: string;
	displayName: string;
	description: string;
	priceMonthly: number;
	priceYearly?: number;
	features: {
		customDomains: boolean;
		ssoIntegration: boolean;
		advancedAnalytics: boolean;
		prioritySupport: boolean;
		auditLogs: boolean;
		webhooks: boolean;
		advancedSecurity: boolean;
		crossTenantSharing: boolean;
	};
}

interface UsageMetrics {
	users: { current: number; limit: number; percentage: number };
	packages: { current: number; limit: number; percentage: number };
	storage: { current: number; limit: number; percentage: number };
	bandwidth: { current: number; limit: number; percentage: number };
	api_calls: { current: number; limit: number; percentage: number };
}

interface TenantContextType {
	tenant: Tenant | null;
	subscription: Subscription | null;
	plan: Plan | null;
	usage: UsageMetrics | null;
	isLoading: boolean;
	refreshTenant: () => Promise<void>;
	updateTenant: (data: Partial<Tenant>) => Promise<void>;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

export function useTenant() {
	const context = useContext(TenantContext);
	if (context === undefined) {
		throw new Error("useTenant must be used within a TenantProvider");
	}
	return context;
}

interface TenantProviderProps {
	children: React.ReactNode;
}

export function TenantProvider({ children }: TenantProviderProps) {
	const [tenant, setTenant] = useState<Tenant | null>(null);
	const [subscription, setSubscription] = useState<Subscription | null>(null);
	const [plan, setPlan] = useState<Plan | null>(null);
	const [usage, setUsage] = useState<UsageMetrics | null>(null);
	const [isLoading, setIsLoading] = useState(false);

	const { token, isAuthenticated } = useAuth();

	const refreshTenant = async () => {
		if (!token) return;

		try {
			setIsLoading(true);

			// Demo mode - provide mock data
			if (token === "demo-jwt-token") {
				// Simulate API delay
				await new Promise((resolve) => setTimeout(resolve, 800));

				const demoTenant: Tenant = {
					id: "tenant_demo",
					name: "Demo Organization",
					slug: "demo",
					companyName: "Artify Demo Corp",
					website: "https://demo.artify.monster",
					description: "Demo organization for testing Artify features",
					avatar: undefined,
					settings: {
						allowPublicPackages: true,
						customDomain: "packages.demo.artify.monster",
						webhookSettings: {
							enabled: false,
							endpoints: [],
						},
						securitySettings: {
							enforceSSO: false,
							allowedDomains: [],
						},
					},
					subscription: {
						plan: "enterprise",
						status: "active",
						billingCycle: "monthly",
						currentPeriodStart: new Date().toISOString(),
						currentPeriodEnd: new Date(
							Date.now() + 30 * 24 * 60 * 60 * 1000,
						).toISOString(),
						limits: {
							packageStorage: 1000000000,
							monthlyDownloads: 100000,
							teamMembers: 50,
						},
						usage: {
							packageStorage: 125000000,
							monthlyDownloads: 12500,
							teamMembers: 8,
						},
					},
					createdAt: new Date(
						Date.now() - 365 * 24 * 60 * 60 * 1000,
					).toISOString(),
					updatedAt: new Date().toISOString(),
				};

				const demoSubscription: Subscription = {
					id: "sub_demo",
					planId: "plan_team",
					status: "active",
					billingCycle: "monthly",
					currentPeriodStart: new Date().toISOString(),
					currentPeriodEnd: new Date(
						Date.now() + 30 * 24 * 60 * 60 * 1000,
					).toISOString(),
					trialEnd: undefined,
					cancelAtPeriodEnd: false,
				};

				const demoPlan: Plan = {
					id: "plan_team",
					name: "team",
					displayName: "Team",
					description: "For growing teams with advanced needs",
					priceMonthly: 49,
					priceYearly: 490,
					features: {
						customDomains: true,
						ssoIntegration: true,
						advancedAnalytics: true,
						prioritySupport: true,
						auditLogs: true,
						webhooks: true,
						advancedSecurity: false,
						crossTenantSharing: false,
					},
				};

				const demoUsage: UsageMetrics = {
					users: { current: 5, limit: 25, percentage: 20 },
					packages: { current: 23, limit: 1000, percentage: 2.3 },
					storage: {
						current: 1.2 * 1024 * 1024 * 1024,
						limit: 100 * 1024 * 1024 * 1024,
						percentage: 1.2,
					}, // 1.2GB / 100GB
					bandwidth: {
						current: 45 * 1024 * 1024 * 1024,
						limit: 500 * 1024 * 1024 * 1024,
						percentage: 9,
					}, // 45GB / 500GB
					api_calls: { current: 15420, limit: 100000, percentage: 15.4 },
				};

				setTenant(demoTenant);
				setSubscription(demoSubscription);
				setPlan(demoPlan);
				setUsage(demoUsage);
				localStorage.setItem("artify_tenant", JSON.stringify(demoTenant));
				setIsLoading(false);
				return;
			}

			// Get current tenant from localStorage or API
			const cachedTenant = localStorage.getItem("artify_tenant");
			let currentTenantId: string;

			if (cachedTenant) {
				const parsed = JSON.parse(cachedTenant);
				currentTenantId = parsed.id;
			} else {
				// For demo purposes, we'll use a default tenant
				// In real implementation, get from user's default tenant or tenant selection
				currentTenantId = "tenant_demo";
			}

			// Fetch tenant info, subscription, and usage in parallel
			const [tenantResponse, subscriptionResponse, usageResponse] =
				await Promise.all([
					fetch(
						`${import.meta.env.VITE_API_BASE_URL || "https://tenant.artify.monster"}/api/t/${currentTenantId}/info`,
						{
							headers: {
								Authorization: `Bearer ${token}`,
							},
						},
					),
					fetch(
						`${import.meta.env.VITE_API_BASE_URL || "https://tenant.artify.monster"}/api/t/${currentTenantId}/billing/subscription`,
						{
							headers: {
								Authorization: `Bearer ${token}`,
							},
						},
					),
					fetch(
						`${import.meta.env.VITE_API_BASE_URL || "https://tenant.artify.monster"}/api/t/${currentTenantId}/billing/usage`,
						{
							headers: {
								Authorization: `Bearer ${token}`,
							},
						},
					),
				]);

			if (tenantResponse.ok) {
				const tenantData = await tenantResponse.json();
				setTenant(tenantData);
				localStorage.setItem("artify_tenant", JSON.stringify(tenantData));
			}

			if (subscriptionResponse.ok) {
				const subscriptionData = await subscriptionResponse.json();
				if (subscriptionData.subscription) {
					setSubscription(subscriptionData.subscription);
					setPlan(subscriptionData.plan);
				}
			}

			if (usageResponse.ok) {
				const usageData = await usageResponse.json();
				setUsage(usageData.usage);
			}
		} catch (error) {
			console.error("Failed to fetch tenant data:", error);
		} finally {
			setIsLoading(false);
		}
	};

	// Load tenant data when authenticated
	useEffect(() => {
		if (isAuthenticated && token) {
			refreshTenant();
		}
	}, [isAuthenticated, token, refreshTenant]);

	const updateTenant = async (data: Partial<Tenant>) => {
		if (!token || !tenant) return;

		try {
			const response = await fetch(`/api/t/${tenant.id}/admin/tenant`, {
				method: "PATCH",
				headers: {
					Authorization: `Bearer ${token}`,
					"Content-Type": "application/json",
				},
				body: JSON.stringify(data),
			});

			if (!response.ok) {
				throw new Error("Failed to update tenant");
			}

			// Update local state
			const updatedTenant = { ...tenant, ...data };
			setTenant(updatedTenant);
			localStorage.setItem("artify_tenant", JSON.stringify(updatedTenant));
		} catch (error) {
			console.error("Failed to update tenant:", error);
			throw error;
		}
	};

	const value: TenantContextType = {
		tenant,
		subscription,
		plan,
		usage,
		isLoading,
		refreshTenant,
		updateTenant,
	};

	return (
		<TenantContext.Provider value={value}>{children}</TenantContext.Provider>
	);
}
